import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import DataTable from '../components/common/Table';
import Modal from '../components/common/Modal';
import Pagination from '../components/common/Pagination';
import FilterBar from '../components/common/FilterBar';
import { toast } from '../components/common/Toast';
import { usePermission } from '../hooks/usePermission';
import * as companiesApi from '../api/companies';
import { Company, CompanyStatus } from '../types';

type StatusFilter = 'all' | CompanyStatus;

const STATUS_LABELS: Record<StatusFilter, string> = {
  all: '전체',
  approved: '승인됨',
  pending: '승인 대기',
  rejected: '거부됨',
};

const errMsg = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { error?: string } | undefined;
    if (data?.error) return data.error;
  }
  return fallback;
};

const CompaniesPage: React.FC = () => {
  const { canAccess } = usePermission();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight') || '';

  const [companies, setCompanies] = useState<Company[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  // ISS-025: 검색 debounce — FilterBar 입력값을 400ms 지연 후 서버로 전송
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    business_number: '',
    phone: '',
    address: '',
  });

  const [editTarget, setEditTarget] = useState<Company | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    business_number: '',
    phone: '',
    address: '',
  });

  const canManage = canAccess('companies_manage');

  const load = useCallback(async () => {
    try {
      const res = await companiesApi.getList({
        page,
        limit: 20,
        status,
        search: debouncedSearch,
      });
      setCompanies(res.items);
      setTotal(res.total);
    } catch (err) {
      toast.error(errMsg(err, '회사 목록을 불러올 수 없습니다'));
    }
  }, [page, status, debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  // ISS-025: 400ms debounce — 검색어 타이핑 중 과도한 API 호출 방지
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!highlightId || companies.length === 0) return;
    if (!companies.find((c) => c.id === highlightId)) {
      setStatus('pending');
      setPage(1);
    }
  }, [highlightId, companies]);

  // ---- create ----
  const openCreate = () => {
    setCreateForm({ name: '', business_number: '', phone: '', address: '' });
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!createForm.name.trim()) {
      toast.error('회사명을 입력해주세요');
      return;
    }
    try {
      await companiesApi.create({
        name: createForm.name.trim(),
        business_number: createForm.business_number || undefined,
        phone: createForm.phone || undefined,
        address: createForm.address || undefined,
      });
      toast.success('회사가 등록되었습니다 (자동 승인)');
      setCreateOpen(false);
      load();
    } catch (err) {
      toast.error(errMsg(err, '회사 등록에 실패했습니다'));
    }
  };

  // ---- edit ----
  const openEdit = (c: Company) => {
    setEditTarget(c);
    setEditForm({
      name: c.name,
      business_number: c.business_number || '',
      phone: c.phone || '',
      address: c.address || '',
    });
  };

  const handleEditSave = async () => {
    if (!editTarget) return;
    try {
      await companiesApi.update(editTarget.id, {
        name: editForm.name.trim(),
        business_number: editForm.business_number || undefined,
        phone: editForm.phone || undefined,
        address: editForm.address || undefined,
      });
      toast.success('회사 정보가 저장되었습니다');
      setEditTarget(null);
      load();
    } catch (err) {
      toast.error(errMsg(err, '저장에 실패했습니다'));
    }
  };

  // ---- approve / reject / recover / permanent delete ----
  const handleApprove = async (c: Company) => {
    if (!window.confirm(`"${c.name}"을(를) 승인하시겠습니까?`)) return;
    try {
      await companiesApi.approve(c.id);
      toast.success('승인되었습니다');
      if (highlightId === c.id) {
        searchParams.delete('highlight');
        setSearchParams(searchParams);
      }
      load();
    } catch (err) {
      toast.error(errMsg(err, '승인에 실패했습니다'));
    }
  };

  const handleReject = async (c: Company) => {
    if (!window.confirm(`"${c.name}"을(를) 거부하시겠습니까?\n레코드는 보존되고 "거부됨" 탭으로 이동합니다.`)) return;
    try {
      await companiesApi.reject(c.id);
      toast.success('거부되었습니다 (거부됨 탭에서 복구/영구삭제 가능)');
      if (highlightId === c.id) {
        searchParams.delete('highlight');
        setSearchParams(searchParams);
      }
      load();
    } catch (err) {
      toast.error(errMsg(err, '거부에 실패했습니다'));
    }
  };

  const handleRecover = async (c: Company) => {
    if (!window.confirm(`"${c.name}"을(를) 승인됨으로 복구하시겠습니까?`)) return;
    try {
      await companiesApi.recover(c.id);
      toast.success('복구되었습니다 (승인됨 탭으로 이동)');
      load();
    } catch (err) {
      toast.error(errMsg(err, '복구에 실패했습니다'));
    }
  };

  const handlePermanentDelete = async (c: Company) => {
    if (!window.confirm(`"${c.name}"을(를) 영구 삭제하시겠습니까?\n연결된 사용자의 소속이 해제됩니다. 이 동작은 되돌릴 수 없습니다.`)) return;
    try {
      const res = await companiesApi.permanentDelete(c.id);
      toast.success(`영구 삭제되었습니다 (영향 user: ${res.affected_users}명)`);
      load();
    } catch (err) {
      toast.error(errMsg(err, '영구 삭제에 실패했습니다'));
    }
  };

  // ---- delete (approved) — super_admin 전용 ----
  const handleDelete = async (c: Company) => {
    if (!window.confirm(`"${c.name}"을(를) 삭제하시겠습니까?\n연결된 사용자의 소속이 해제됩니다.`)) return;
    try {
      const res = await companiesApi.remove(c.id);
      toast.success(`삭제되었습니다 (영향 user: ${res.affected_users}명)`);
      load();
    } catch (err) {
      toast.error(errMsg(err, '삭제에 실패했습니다'));
    }
  };

  const renderStatusBadge = (c: Company) => {
    if (c.status === 'approved') return <span className="badge badge--green">승인됨</span>;
    if (c.status === 'pending') return <span className="badge badge--orange">승인 대기</span>;
    return <span className="badge badge--red">거부됨</span>;
  };

  const columns = [
    {
      key: 'name',
      label: '회사명',
      render: (c: Company) => (
        <span>
          {c.name}
          {c.id === highlightId && (
            <span
              style={{
                marginLeft: 8,
                padding: '2px 6px',
                borderRadius: 4,
                background: '#fff3cd',
                color: '#856404',
                fontSize: 11,
              }}
            >
              Alert
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'status',
      label: '상태',
      render: renderStatusBadge,
    },
    {
      key: 'rejected_by_display',
      label: '거부자',
      render: (c: Company) =>
        c.status === 'rejected' ? (
          <span style={{ fontSize: 12, color: '#666' }}>{c.rejected_by_display || '-'}</span>
        ) : (
          '-'
        ),
    },
    { key: 'business_number', label: '사업자번호', render: (c: Company) => c.business_number || '-' },
    { key: 'phone', label: '대표번호', render: (c: Company) => c.phone || '-' },
    { key: 'created_at', label: '등록일', render: (c: Company) => new Date(c.created_at).toLocaleDateString('ko-KR') },
    {
      key: 'actions',
      label: '',
      render: (c: Company) => {
        if (!canManage) return null;
        return (
          <div className="table-actions">
            {c.status === 'pending' && (
              <>
                <button
                  className="btn btn--sm btn--primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleApprove(c);
                  }}
                >
                  승인
                </button>
                <button
                  className="btn btn--sm btn--warning"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReject(c);
                  }}
                >
                  거부
                </button>
              </>
            )}
            {c.status === 'rejected' && (
              <>
                <button
                  className="btn btn--sm btn--primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRecover(c);
                  }}
                >
                  복구(승인)
                </button>
                <button
                  className="btn btn--sm btn--danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePermanentDelete(c);
                  }}
                >
                  영구 삭제
                </button>
              </>
            )}
            {c.status !== 'rejected' && (
              <button
                className="btn btn--sm btn--secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  openEdit(c);
                }}
              >
                수정
              </button>
            )}
            {c.status === 'approved' && (
              <button
                className="btn btn--sm btn--danger"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(c);
                }}
              >
                삭제
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h2>회사 관리</h2>
        {canManage && (
          <button className="btn btn--primary" onClick={openCreate}>
            회사 등록
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {(['all', 'approved', 'pending', 'rejected'] as StatusFilter[]).map((s) => (
          <button
            key={s}
            className={`btn btn--sm ${status === s ? 'btn--primary' : 'btn--secondary'}`}
            onClick={() => {
              setStatus(s);
              setPage(1);
            }}
          >
            {STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      <FilterBar onSearch={setSearch} searchPlaceholder="회사명 검색..." />
      <DataTable columns={columns} data={companies} />
      <Pagination page={page} total={total} limit={20} onPageChange={setPage} />

      {/* Create */}
      <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)} title="회사 등록 (자동 승인)" onConfirm={handleCreate}>
        <div className="form-group">
          <label>회사명 *</label>
          <input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} />
        </div>
        <div className="form-group">
          <label>사업자등록번호</label>
          <input
            value={createForm.business_number}
            onChange={(e) => setCreateForm({ ...createForm, business_number: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>대표번호</label>
          <input value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} />
        </div>
        <div className="form-group">
          <label>주소</label>
          <input value={createForm.address} onChange={(e) => setCreateForm({ ...createForm, address: e.target.value })} />
        </div>
      </Modal>

      {/* Edit */}
      <Modal
        isOpen={!!editTarget}
        onClose={() => setEditTarget(null)}
        title={editTarget ? `"${editTarget.name}" 수정` : '회사 수정'}
        onConfirm={handleEditSave}
      >
        <div className="form-group">
          <label>회사명</label>
          <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
        </div>
        <div className="form-group">
          <label>사업자등록번호</label>
          <input
            value={editForm.business_number}
            onChange={(e) => setEditForm({ ...editForm, business_number: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>대표번호</label>
          <input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
        </div>
        <div className="form-group">
          <label>주소</label>
          <input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} />
        </div>
      </Modal>

      {!canManage && <p>회사 관리 권한이 없습니다.</p>}
    </div>
  );
};

export default CompaniesPage;
